<template>
  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>Bypass</span>
        </span>
        <span class="sba-collection-context">Enrollee Upload Workspace</span>
      </div>
    </div>
  </nav>

  <div class="sba-collection-page-surface is-workbench bypass-secondary-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="bypass-page-surface sba-slide-in" v-else>
        <div class="bypass-review-head">
          <div class="bypass-title-block">
            <label class="active teal-text">Bypass Upload</label>
            <h5>Load Enrollee List</h5>
            <p>Upload an approved enrollee spreadsheet, attach it to a scheme, and select the coverage period for processing.</p>
          </div>
          <span class="bypass-scope-chip">
            Excel only
            <em>.xlsx or .xls files</em>
          </span>
        </div>

        <form @submit.prevent="handleSubmit" autocomplete="off">
          <div class="bypass-form-stack">
            <div class="bypass-form-panel">
              <div class="panel-copy">
                <label class="active teal-text">Step 1</label>
                <h6>Select spreadsheet</h6>
                <p>Choose the enrollee list prepared for bypass loading.</p>
              </div>
              <div class="file-field input-field">
                <div class="btn black">
                  <span>New List</span>
                  <input type="file" @change="handleFile" />
                </div>
                <div class="file-path-wrapper">
                  <input class="file-path" v-model="inputFile" type="text" autocomplete="off" />
                  <p class="red-text" v-if="fileError">{{ fileError }}</p>
                </div>
              </div>
            </div>

            <div class="bypass-form-panel" v-if="file">
              <div class="panel-copy">
                <label class="active teal-text">Step 2</label>
                <h6>Attach scheme</h6>
                <p>Select the scheme this list belongs to.</p>
              </div>
              <div class="input-field">
                <select v-model="scheme">
                  <option value="" disabled selected></option>
                  <option :value="scheme.id" v-for="scheme in allSchemes" :key="scheme.id">
                    {{ scheme.name }}
                  </option>
                </select>
                <label>Scheme of List</label>
              </div>
            </div>

            <div class="bypass-form-panel" v-if="file && scheme">
              <div class="panel-copy">
                <label class="active teal-text">Step 3</label>
                <h6>Coverage start</h6>
                <p>Choose the first month covered by this list.</p>
              </div>
              <div class="input-field" @click="(periodStart = null), (periodEnd = null)">
                <select v-model="periodStart">
                  <option value="" disabled selected></option>
                  <option :value="nextMonthDate">{{ nextMonth }}</option>
                  <option :value="thisMonthDate">
                    {{ thisMonth }} (Current month)
                  </option>
                  <option value="">-</option>
                  <option :value="pastMonth.date" v-for="pastMonth in pastInactiveMonths" :key="pastMonth.date">
                    {{ pastMonth.month }}
                  </option>
                </select>
                <label>Period of List</label>
              </div>
            </div>

            <div class="bypass-form-panel" v-if="file && scheme && periodStart">
              <div class="panel-copy">
                <label class="active teal-text">Step 4</label>
                <h6>Coverage end</h6>
                <p>Choose the last month covered by this list.</p>
              </div>
              <div class="input-field">
                <select v-model="periodEnd">
                  <option value="" disabled selected></option>
                  <option :value="endMonth.date" v-for="endMonth in endMonths" :key="endMonth.date">
                    {{ endMonth.month }}
                  </option>
                </select>
                <label>Select End Month</label>
              </div>
            </div>

            <div class="bypass-upload-error" v-if="uploadError">
              {{ uploadError }}
            </div>

            <div class="bypass-submit-strip">
              <button
                type="button"
                class="btn-flat bypass-cancel-button"
                :class="{ disabled: isSubmitting }"
                @click.prevent="clearForm"
              >
                Cancel
              </button>
              <button class="waves-effect waves-light btn-large" :class="{
                disabled:
                  !scheme || !periodStart || !periodEnd || isSubmitting,
              }">
                <i class="material-icons left">upload</i>{{ isSubmitting ? `Uploading (${uploadProgress})` :
                  "Upload"
                }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUpdated, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItems from "@/composables/getItems";
import useStorage from "@/composables/useStorage";
import {
  activeMonths,
  pastMonths,
  generateEndMonths,
} from "@/composables/fxLibrary/months";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import M from "materialize-css";

export default {
  name: "bypassHome",
  components: { SubHeading, LoadingDiv },
  setup() {
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const scheme = ref(null);
    const allSchemes = ref([]);
    const thisMonth = ref(null);
    const nextMonth = ref(null);
    const thisMonthDate = ref(null);
    const nextMonthDate = ref(null);
    const pastInactiveMonths = ref([]);
    const endMonths = ref([]);
    const periodStart = ref(null);
    const periodEnd = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    const router = useRouter();
    const { scrollToModuleTop } = useModuleNavigation();

    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    onMounted(() => {
      document.title = "Bypass";
    });

    onMounted(() => {
      tabs.value.push({ id: 1, name: "Bypass", title: "All", icon: null, url: "bypass", hash: null });
      currTab.value = tabs.value.find((x) => x.id === 1);
      home.value = currTab.value;
    });

    const handleActive = () => {
      clearForm();
      scrollToModuleTop();
    };

    onUpdated(() => {
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    onMounted(() => {
      (async () => {
        const { res } = await getItems("scheme/filter");
        if (res.value) {
          allSchemes.value = res.value.data;
        }
        isReady.value = true;
      })();
    });

    onMounted(() => {
      const { fx: active } = activeMonths();
      thisMonth.value = active.value.thisMonth;
      thisMonthDate.value = active.value.thisMonthDate;
      nextMonth.value = active.value.nextMonth;
      nextMonthDate.value = active.value.nextMonthDate;

      const { fx: past } = pastMonths();
      pastInactiveMonths.value = past.value;
    });

    const clearForm = () => {
      file.value = null;
      inputFile.value = null;
      isSubmitting.value = false;
      uploadProgress.value = "0%";
      fileError.value = null;
      uploadError.value = null;
      scheme.value = null;
      periodStart.value = null;
      periodEnd.value = null;
    };

    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
      scheme.value = null;
      periodStart.value = null;
      periodEnd.value = null;
    };

    watchEffect(() => {
      if (periodStart.value) {
        const { fx: endings } = generateEndMonths(periodStart.value);
        endMonths.value = endings.value;
      } else {
        endMonths.value = [];
      }
    });

    const handleSubmit = async () => {
      if (
        file.value &&
        scheme.value &&
        (periodStart.value || periodEnd.value)
      ) {
        isSubmitting.value = true;
        uploadError.value = null;

        const fx = "enrolleeLoader";

        const cloudInput = {
          scheme: scheme.value,
          start: periodStart.value,
          end: periodEnd.value,
        };

        uploadFile(file.value, fx, cloudInput);
      } else {
        clearForm();
      }
    };

    watchEffect(() => {
      if (completed.value) {
        clearForm();
        router.push({
          name: "bypass",
          rows: data.value.succ.inserted_rows,
        });
      }
      if (uploadError.value) {
        clearForm();
      }
    });

    return {
      handleActive,
      clearForm,
      handleFile,
      home,
      currTab,
      tabs,
      isReady,
      isSubmitting,
      handleSubmit,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      allSchemes,
      scheme,
      thisMonth,
      thisMonthDate,
      nextMonth,
      nextMonthDate,
      pastInactiveMonths,
      endMonths,
      periodStart,
      periodEnd,
    };
  },
};
</script>

<style scoped>
.bypass-page-surface {
  margin-top: 0;
  position: relative;
  z-index: 1;
}

.bypass-review-head,
.bypass-form-panel,
.bypass-upload-error,
.bypass-submit-strip {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.bypass-secondary-surface {
  min-height: calc(100vh - 260px);
}

.bypass-review-head {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.bypass-review-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.bypass-review-head > * {
  position: relative;
  z-index: 2;
}

.bypass-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.bypass-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 760px;
}

.bypass-scope-chip {
  background: #e0f2f1;
  border: 1px solid #b2dfdb;
  border-radius: 18px;
  color: #00695c;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.3;
  padding: 7px 12px;
  text-align: right;
}

.bypass-scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.bypass-form-stack {
  display: grid;
  gap: 0;
}

.bypass-form-panel,
.bypass-submit-strip,
.bypass-upload-error {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-top: 0;
  padding: 20px 26px;
}

.bypass-form-panel {
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr);
}

.bypass-form-panel:last-of-type {
  border-radius: 0;
}

.panel-copy h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.panel-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 5px 0 0;
}

.bypass-form-panel .input-field,
.bypass-form-panel .file-field {
  margin: 0;
}

.bypass-submit-strip {
  background: #f7f7f7;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  text-align: right;
}

.bypass-submit-strip button {
  min-width: 190px;
}

.bypass-cancel-button {
  color: #455a64;
  font-weight: 600;
  min-width: auto !important;
  text-transform: none;
}

.bypass-upload-error {
  color: #c62828;
  font-weight: 600;
}

@media only screen and (max-width: 992px) {
  .bypass-review-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .bypass-form-panel {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .bypass-submit-strip {
    justify-content: flex-start;
    text-align: left;
  }
}

@media only screen and (max-width: 600px) {
  .bypass-secondary-surface {
    padding-bottom: 34px;
  }

  .bypass-review-head {
    gap: 14px;
    padding: 20px;
  }

  .bypass-title-block h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .bypass-title-block p {
    font-size: 0.95rem;
  }

  .bypass-scope-chip {
    text-align: left;
    width: 100%;
  }

  .bypass-form-panel,
  .bypass-submit-strip,
  .bypass-upload-error {
    padding: 18px;
  }

  .bypass-submit-strip {
    align-items: stretch;
    flex-direction: column;
  }

  .bypass-submit-strip button,
  .bypass-cancel-button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bypass-review-head,
  .bypass-form-panel,
  .bypass-upload-error,
  .bypass-submit-strip {
    animation: none;
  }
}
</style>
